import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import { API_BASE_URL } from '../../config/api';
import * as adsService from '../../services/adsService';
import './AdminAdsManagePage.css';

const AdminAdsManagePage = () => {
  const [activeTab, setActiveTab] = useState('active');
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [placementsLoading, setPlacementsLoading] = useState(false);

  // Data
  const [campaigns, setCampaigns] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [stats, setStats] = useState({ totalRevenue: 0, activeAds: 0, totalViews: 0, totalClicks: 0 });

  // Modals
  const [showPlacementModal, setShowPlacementModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Editing state
  const [editingPlacement, setEditingPlacement] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [viewingAd, setViewingAd] = useState(null);
  const [previewPlacement, setPreviewPlacement] = useState(null);

  // Form states
  const [placementForm, setPlacementForm] = useState({ name: '', price: '', dimensions: '', status: 'active', description: '' });
  const [campaignForm, setCampaignForm] = useState({ companyName: '', placementId: '', startDate: '', endDate: '', price: '', linkUrl: '', notes: '', paymentStatus: 'unpaid', image: null });
  const [imagePreview, setImagePreview] = useState(null);

  // Fetch all data
  const fetchData = useCallback(async (retryCount = 0, silent = false) => {
    try {
      if (!silent) setLoading(true);
      setFetchError(null);
      const [campaignsData, placementsData, statsData] = await Promise.all([
        adsService.getCampaigns(),
        adsService.getPlacements(),
        adsService.getStats()
      ]);
      setCampaigns(Array.isArray(campaignsData) ? campaignsData : []);
      setPlacements(Array.isArray(placementsData) ? placementsData : []);
      setStats(statsData || { totalRevenue: 0, activeAds: 0, totalViews: 0, totalClicks: 0 });
    } catch (error) {
      console.error('Error fetching ads data:', error);
      // Auto-retry once after 2s if server was unreachable
      if (retryCount === 0 && error.message && error.message.includes('unreachable')) {
        setTimeout(() => fetchData(1), 2000);
        return;
      }
      // Only show error banner; optimistic state from prior operations is preserved
      setFetchError(error.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Fetch placements only (used when modal opens and placements not yet loaded)
  const fetchPlacements = useCallback(async () => {
    try {
      setPlacementsLoading(true);
      const data = await adsService.getPlacements();
      setPlacements(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching placements:', error);
    } finally {
      setPlacementsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Helpers
  const formatNumber = (n) => {
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return String(n);
  };

  const formatCurrency = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0 });

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';

  const formatDateInput = (d) => d ? new Date(d).toISOString().split('T')[0] : '';

  const getStatusClass = (status) => {
    switch (status) {
      case 'active': case 'Running': return 'status-active';
      case 'inactive': case 'paused': case 'cancelled': return 'status-inactive';
      case 'pending': return 'status-warning';
      case 'expired': return 'status-expired';
      default: return '';
    }
  };

  const getStatusLabel = (ad) => {
    if (!ad.status) return '';
    const now = new Date();
    const end = new Date(ad.endDate);
    const diffDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    if (ad.status === 'active' && diffDays <= 7 && diffDays > 0) return 'Ending Soon';
    return ad.status.charAt(0).toUpperCase() + ad.status.slice(1);
  };

  // ==================== PLACEMENT CRUD ====================
  const openAddPlacement = () => {
    setEditingPlacement(null);
    setPlacementForm({ name: '', price: '', dimensions: '', status: 'active', description: '' });
    setShowPlacementModal(true);
  };

  const openEditPlacement = (p) => {
    setEditingPlacement(p);
    setPlacementForm({ name: p.name, price: String(p.price || ''), dimensions: p.dimensions || '', status: p.status, description: p.description || '' });
    setShowPlacementModal(true);
  };

  const handleSavePlacement = async () => {
    if (!placementForm.name) return alert('Placement name is required');
    try {
      if (editingPlacement) {
        const updated = await adsService.updatePlacement(editingPlacement.id, placementForm);
        setPlacements(prev => prev.map(p => p.id === editingPlacement.id ? { ...p, ...updated } : p));
      } else {
        const created = await adsService.createPlacement(placementForm);
        setPlacements(prev => [...prev, created]);
      }
      setShowPlacementModal(false);
      fetchData(0, true);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDeletePlacement = async (id) => {
    if (!window.confirm('Delete this placement and all its ads?')) return;
    try {
      await adsService.deletePlacement(id);
      setPlacements(prev => prev.filter(p => p.id !== id));
      fetchData(0, true);
    } catch (error) {
      alert(error.message);
    }
  };

  // ==================== CAMPAIGN CRUD ====================
  const openAddCampaign = () => {
    setEditingCampaign(null);
    setCampaignForm({ companyName: '', placementId: '', startDate: '', endDate: '', price: '', linkUrl: '', notes: '', paymentStatus: 'unpaid', image: null });
    setImagePreview(null);
    setShowCampaignModal(true);
    // Re-fetch placements if not loaded yet
    if (placements.length === 0) fetchPlacements();
  };

  const openEditCampaign = (ad) => {
    setEditingCampaign(ad);
    setCampaignForm({
      companyName: ad.companyName,
      placementId: ad.placementId,
      startDate: formatDateInput(ad.startDate),
      endDate: formatDateInput(ad.endDate),
      price: String(ad.price || ''),
      linkUrl: ad.linkUrl || '',
      notes: ad.notes || '',
      paymentStatus: ad.paymentStatus || 'unpaid',
      image: null
    });
    setImagePreview(ad.imageUrl ? (ad.imageUrl.startsWith('http') ? ad.imageUrl : `${API_BASE_URL}${ad.imageUrl}`) : null);
    setShowCampaignModal(true);
  };

  const openViewAd = (ad) => {
    setViewingAd(ad);
    setShowViewModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCampaignForm(prev => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSaveCampaign = async () => {
    if (!campaignForm.companyName || !campaignForm.placementId || !campaignForm.startDate || !campaignForm.endDate) {
      return alert('Company name, placement, start date, and end date are required');
    }
    try {
      const fd = new FormData();
      fd.append('companyName', campaignForm.companyName);
      fd.append('placementId', campaignForm.placementId);
      fd.append('startDate', campaignForm.startDate);
      fd.append('endDate', campaignForm.endDate);
      fd.append('price', campaignForm.price);
      fd.append('linkUrl', campaignForm.linkUrl);
      fd.append('notes', campaignForm.notes);
      fd.append('paymentStatus', campaignForm.paymentStatus);
      if (campaignForm.image) fd.append('image', campaignForm.image);

      if (editingCampaign) {
        const updated = await adsService.updateCampaign(editingCampaign.id, fd);
        setCampaigns(prev => prev.map(c => c.id === editingCampaign.id ? { ...c, ...updated } : c));
      } else {
        const created = await adsService.createCampaign(fd);
        setCampaigns(prev => [created, ...prev]);
      }
      setShowCampaignModal(false);
      fetchData(0, true);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDeleteCampaign = async (id) => {
    if (!window.confirm('Delete this advertisement?')) return;
    try {
      await adsService.deleteCampaign(id);
      setCampaigns(prev => prev.filter(c => c.id !== id));
      if (viewingAd?.id === id) setShowViewModal(false);
      fetchData(0, true);
    } catch (error) {
      alert(error.message);
    }
  };

  // Preview placement
  const openPreview = (p) => {
    setPreviewPlacement(p);
    setShowPreviewModal(true);
  };

  return (
    <AdminLayout>
      <div className="admin-ads-content">
        <div className="ads-page-header">
          <div className="ads-title-section">
            <h1 className="ads-page-title">Ads & Promotions</h1>
            <p className="ads-page-description">Manage advertising placements and promotional campaigns</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="add-placement-btn" style={{ background: '#fff', color: '#1F396C', border: '1px solid #1F396C' }} onClick={openAddCampaign}>
              + New Campaign
            </button>
            <button className="add-placement-btn" onClick={openAddPlacement}>
              + Add Placement
            </button>
          </div>
        </div>

        {/* Error banner with retry */}
        {fetchError && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ color: '#DC2626', fontSize: 14 }}>Failed to load data: {fetchError}</span>
            <button onClick={fetchData} style={{ background: '#DC2626', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>Retry</button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="ads-stats-grid">
          <div className="ads-stat-card">
            <div className="stat-icon revenue">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 1V23M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div className="stat-info">
              <div className="stat-value">{formatCurrency(stats.totalRevenue)}</div>
              <div className="stat-label">Total Revenue</div>
            </div>
          </div>
          <div className="ads-stat-card">
            <div className="stat-icon active">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M17 8L12 3M12 3L7 8M12 3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.activeAds}</div>
              <div className="stat-label">Active Ads</div>
            </div>
          </div>
          <div className="ads-stat-card">
            <div className="stat-icon views">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div className="stat-info">
              <div className="stat-value">{formatNumber(stats.totalViews)}</div>
              <div className="stat-label">Total Views</div>
            </div>
          </div>
          <div className="ads-stat-card">
            <div className="stat-icon clicks">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div className="stat-info">
              <div className="stat-value">{formatNumber(stats.totalClicks)}</div>
              <div className="stat-label">Total Clicks</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="ads-tabs">
          <button className={`ads-tab ${activeTab === 'active' ? 'active' : ''}`} onClick={() => setActiveTab('active')}>Active Campaigns</button>
          <button className={`ads-tab ${activeTab === 'placements' ? 'active' : ''}`} onClick={() => setActiveTab('placements')}>Ad Placements</button>
        </div>

        {/* Active Campaigns Tab */}
        {activeTab === 'active' && (
          <div className="ads-table-container">
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading campaigns...</div>
            ) : campaigns.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                <p>No campaigns yet.</p>
                <button onClick={openAddCampaign} style={{ marginTop: 12, padding: '8px 20px', background: '#1F396C', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Create First Campaign</button>
              </div>
            ) : (
              <table className="ads-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Placement</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Amount</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th>Views / Clicks</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((ad) => (
                    <tr key={ad.id}>
                      <td className="company-name">{ad.companyName}</td>
                      <td>{ad.placement?.name || '—'}</td>
                      <td>{formatDate(ad.startDate)}</td>
                      <td>{formatDate(ad.endDate)}</td>
                      <td className="amount">{formatCurrency(ad.price)}</td>
                      <td>
                        <span className={`status-badge ${ad.paymentStatus === 'paid' ? 'status-active' : 'status-warning'}`}>
                          {ad.paymentStatus}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusClass(ad.status)} ${getStatusLabel(ad) === 'Ending Soon' ? 'status-warning' : ''}`}>
                          {getStatusLabel(ad)}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>{formatNumber(ad.views)} / {formatNumber(ad.clicks)}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="action-btn view-btn" title="View" onClick={() => openViewAd(ad)}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 8C1 8 3.5 3 8 3C12.5 3 15 8 15 8C15 8 12.5 13 8 13C3.5 13 1 8 1 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </button>
                          <button className="action-btn edit-btn" title="Edit" onClick={() => openEditCampaign(ad)}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.3879 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.383 14.4084 2.61178C14.5032 2.84055 14.552 3.08575 14.552 3.33337C14.552 3.58099 14.5032 3.82619 14.4084 4.05497C14.3137 4.28374 14.1748 4.49161 13.9997 4.66671L4.99967 13.6667L1.33301 14.6667L2.33301 11L11.333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </button>
                          <button className="action-btn" title="Delete" onClick={() => handleDeleteCampaign(ad.id)} style={{ color: '#ef4444' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Placements Tab */}
        {activeTab === 'placements' && (
          <div className="placements-grid">
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', gridColumn: '1 / -1' }}>Loading placements...</div>
            ) : placements.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', gridColumn: '1 / -1' }}>
                <p>No placements yet.</p>
                <button onClick={openAddPlacement} style={{ marginTop: 12, padding: '8px 20px', background: '#1F396C', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Add First Placement</button>
              </div>
            ) : (
              placements.map((p) => (
                <div key={p.id} className="placement-card">
                  <div className="placement-header">
                    <h3 className="placement-name">{p.name}</h3>
                    <span className={`placement-status ${getStatusClass(p.status)}`}>{p.status === 'active' ? 'Active' : 'Inactive'}</span>
                  </div>
                  <div className="placement-details">
                    <div className="detail-row"><span className="detail-label">Price:</span><span className="detail-value price">{formatCurrency(p.price)}/month</span></div>
                    <div className="detail-row"><span className="detail-label">Dimensions:</span><span className="detail-value">{p.dimensions || '—'}</span></div>
                    <div className="detail-row"><span className="detail-label">Views:</span><span className="detail-value">{formatNumber(p.views)}</span></div>
                    <div className="detail-row"><span className="detail-label">Clicks:</span><span className="detail-value">{formatNumber(p.clicks)}</span></div>
                  </div>
                  <div className="placement-actions">
                    <button className="edit-placement-btn" onClick={() => openEditPlacement(p)}>Edit</button>
                    <button className="preview-placement-btn" onClick={() => openPreview(p)}>Preview</button>
                    <button className="preview-placement-btn" onClick={() => handleDeletePlacement(p.id)} style={{ color: '#ef4444', borderColor: '#fca5a5' }}>Delete</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* ==================== PLACEMENT MODAL ==================== */}
      {showPlacementModal && (
        <div className="ads-modal-overlay" onClick={() => setShowPlacementModal(false)}>
          <div className="ads-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ads-modal-header">
              <h2>{editingPlacement ? 'Edit Placement' : 'Add New Placement'}</h2>
              <button className="ads-modal-close" onClick={() => setShowPlacementModal(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
            <div className="ads-modal-body">
              <div className="ads-form-group">
                <label>Placement Name *</label>
                <input type="text" placeholder="e.g., Homepage Hero Banner" value={placementForm.name} onChange={(e) => setPlacementForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="ads-form-row">
                <div className="ads-form-group">
                  <label>Price ($/month) *</label>
                  <input type="number" placeholder="500" value={placementForm.price} onChange={(e) => setPlacementForm(f => ({ ...f, price: e.target.value }))} />
                </div>
                <div className="ads-form-group">
                  <label>Dimensions</label>
                  <input type="text" placeholder="e.g., 1920x600px" value={placementForm.dimensions} onChange={(e) => setPlacementForm(f => ({ ...f, dimensions: e.target.value }))} />
                </div>
              </div>
              <div className="ads-form-group">
                <label>Status</label>
                <select value={placementForm.status} onChange={(e) => setPlacementForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="ads-form-group">
                <label>Description</label>
                <textarea placeholder="Enter placement description..." rows={3} value={placementForm.description} onChange={(e) => setPlacementForm(f => ({ ...f, description: e.target.value }))} />
              </div>
            </div>
            <div className="ads-modal-footer">
              <button className="ads-btn-cancel" onClick={() => setShowPlacementModal(false)}>Cancel</button>
              <button className="ads-btn-save" onClick={handleSavePlacement}>{editingPlacement ? 'Update' : 'Add'} Placement</button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== CAMPAIGN MODAL (Create/Edit) ==================== */}
      {showCampaignModal && (
        <div className="ads-modal-overlay" onClick={() => setShowCampaignModal(false)}>
          <div className="ads-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="ads-modal-header">
              <h2>{editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}</h2>
              <button className="ads-modal-close" onClick={() => setShowCampaignModal(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
            <div className="ads-modal-body">
              <div className="ads-form-group">
                <label>Company Name *</label>
                <input type="text" placeholder="e.g., ABC Steel Works" value={campaignForm.companyName} onChange={(e) => setCampaignForm(f => ({ ...f, companyName: e.target.value }))} />
              </div>
              <div className="ads-form-group">
                <label>Placement *</label>
                <select value={campaignForm.placementId} onChange={(e) => setCampaignForm(f => ({ ...f, placementId: e.target.value }))} disabled={placementsLoading}>
                  <option value="">{placementsLoading ? 'Loading placements...' : 'Select placement...'}</option>
                  {placements.map(p => (
                    <option key={p.id} value={p.id}>{p.name} — {formatCurrency(p.price)}/month{p.status !== 'active' ? ' (inactive)' : ''}</option>
                  ))}
                </select>
              </div>
              <div className="ads-form-row">
                <div className="ads-form-group">
                  <label>Start Date *</label>
                  <input type="date" value={campaignForm.startDate} onChange={(e) => setCampaignForm(f => ({ ...f, startDate: e.target.value }))} />
                </div>
                <div className="ads-form-group">
                  <label>End Date *</label>
                  <input type="date" value={campaignForm.endDate} onChange={(e) => setCampaignForm(f => ({ ...f, endDate: e.target.value }))} />
                </div>
              </div>
              <div className="ads-form-row">
                <div className="ads-form-group">
                  <label>Price ($)</label>
                  <input type="number" placeholder="1500" value={campaignForm.price} onChange={(e) => setCampaignForm(f => ({ ...f, price: e.target.value }))} />
                </div>
                <div className="ads-form-group">
                  <label>Payment Status</label>
                  <select value={campaignForm.paymentStatus} onChange={(e) => setCampaignForm(f => ({ ...f, paymentStatus: e.target.value }))}>
                    <option value="unpaid">Unpaid</option>
                    <option value="paid">Paid</option>
                    <option value="refunded">Refunded</option>
                  </select>
                </div>
              </div>
              <div className="ads-form-group">
                <label>Link URL</label>
                <input type="url" placeholder="https://example.com" value={campaignForm.linkUrl} onChange={(e) => setCampaignForm(f => ({ ...f, linkUrl: e.target.value }))} />
              </div>
              <div className="ads-form-group">
                <label>Banner Image</label>
                <input type="file" accept="image/*" onChange={handleImageChange} />
                {imagePreview && (
                  <div style={{ marginTop: 8 }}>
                    <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  </div>
                )}
              </div>
              <div className="ads-form-group">
                <label>Notes</label>
                <textarea placeholder="Internal notes..." rows={2} value={campaignForm.notes} onChange={(e) => setCampaignForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div className="ads-modal-footer">
              <button className="ads-btn-cancel" onClick={() => setShowCampaignModal(false)}>Cancel</button>
              <button className="ads-btn-save" onClick={handleSaveCampaign}>{editingCampaign ? 'Update' : 'Create'} Campaign</button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== VIEW AD MODAL ==================== */}
      {showViewModal && viewingAd && (
        <div className="ads-modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="ads-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="ads-modal-header">
              <h2>Campaign Details</h2>
              <button className="ads-modal-close" onClick={() => setShowViewModal(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
            <div className="ads-modal-body">
              {viewingAd.imageUrl && (
                <div style={{ marginBottom: 16, textAlign: 'center' }}>
                  <img src={viewingAd.imageUrl.startsWith('http') ? viewingAd.imageUrl : `${API_BASE_URL}${viewingAd.imageUrl}`} alt={viewingAd.companyName} style={{ maxWidth: '100%', maxHeight: 250, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
                <div><div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>Company</div><div style={{ fontWeight: 500 }}>{viewingAd.companyName}</div></div>
                <div><div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>Placement</div><div style={{ fontWeight: 500 }}>{viewingAd.placement?.name || '—'}</div></div>
                <div><div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>Start Date</div><div>{formatDate(viewingAd.startDate)}</div></div>
                <div><div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>End Date</div><div>{formatDate(viewingAd.endDate)}</div></div>
                <div><div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>Price</div><div style={{ fontWeight: 600 }}>{formatCurrency(viewingAd.price)}</div></div>
                <div><div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>Payment</div><div><span className={`status-badge ${viewingAd.paymentStatus === 'paid' ? 'status-active' : 'status-warning'}`}>{viewingAd.paymentStatus}</span></div></div>
                <div><div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>Status</div><div><span className={`status-badge ${getStatusClass(viewingAd.status)}`}>{getStatusLabel(viewingAd)}</span></div></div>
                <div><div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>Views / Clicks</div><div>{formatNumber(viewingAd.views)} / {formatNumber(viewingAd.clicks)}</div></div>
                {viewingAd.linkUrl && <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>Link</div><a href={viewingAd.linkUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', wordBreak: 'break-all' }}>{viewingAd.linkUrl}</a></div>}
                {viewingAd.notes && <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>Notes</div><div>{viewingAd.notes}</div></div>}
              </div>
            </div>
            <div className="ads-modal-footer">
              <button className="ads-btn-cancel" onClick={() => handleDeleteCampaign(viewingAd.id)} style={{ color: '#ef4444', borderColor: '#fca5a5' }}>Delete</button>
              <button className="ads-btn-save" onClick={() => { setShowViewModal(false); openEditCampaign(viewingAd); }}>Edit Campaign</button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== PREVIEW PLACEMENT MODAL ==================== */}
      {showPreviewModal && previewPlacement && (
        <div className="ads-modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="ads-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div className="ads-modal-header">
              <h2>Preview: {previewPlacement.name}</h2>
              <button className="ads-modal-close" onClick={() => setShowPreviewModal(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
            <div className="ads-modal-body" style={{ textAlign: 'center' }}>
              <div style={{ 
                width: '100%',
                maxWidth: parseInt(previewPlacement.dimensions?.split('x')[0]) || 728,
                height: parseInt(previewPlacement.dimensions?.split('x')[1]) || 90,
                maxHeight: 400,
                background: 'linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 100%)',
                border: '2px dashed #94a3b8',
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                color: '#64748b'
              }}>
                <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>{previewPlacement.name}</div>
                <div style={{ fontSize: 14 }}>{previewPlacement.dimensions || 'No dimensions set'}</div>
                <div style={{ fontSize: 12, marginTop: 8, color: '#94a3b8' }}>Ad banner will appear here</div>
              </div>
              {previewPlacement.description && <p style={{ marginTop: 16, color: '#64748b', fontSize: 14 }}>{previewPlacement.description}</p>}
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: 24, fontSize: 13, color: '#64748b' }}>
                <span>Price: <strong>{formatCurrency(previewPlacement.price)}/month</strong></span>
                <span>Active Ads: <strong>{previewPlacement.advertisements?.length || 0}</strong></span>
              </div>
            </div>
            <div className="ads-modal-footer">
              <button className="ads-btn-cancel" onClick={() => setShowPreviewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminAdsManagePage;
